import { Injectable } from "@angular/core";
import { BaseFacade } from "@webapp/core/abstracts/services/base-facade.service";
import { Observable } from "rxjs";
import { IReflection, ReflectionQuestion, IReflectionUpdate, Reflection, ReflectionDTO, ReflectionQuestionType } from "../models/reflections.models";
import { ReflectionsApiService } from "./reflections-api.service";
import { ReflectionsState } from "./reflections-state.service";
import { RequestConfig } from "@webapp/core/abstracts/models/request-config.model";
import { RequestPaging } from "@webapp/core/abstracts/models/request.paging";
import { ICollection } from "@webapp/core/root/models/root.models";
import {
  IDeleteReflectionQuestionEndpointParams,
  IGetTeamReflectionEndpointParams,
  IPatchReflectionEndpointParams,
  IPatchReflectionQuestionEndpointParams,
  IPatchReflectionUpdateEndpointParams,
  IPostReflectionQuestionEndpointParams,
  IPostReflectionUpdateEndpointParams,
} from "../models/reflections-api.models";
import {
  IPatchReflectionPayload,
  IPatchReflectionUpdatePayload,
  IPatchReflectionUpdateResponse,
  IPostReflectionUpdatePayload,
  PostReflectionQuestionPayload,
} from "../models/reflections-facade.models";

@Injectable({
  providedIn: "any",
})
export class ReflectionsFacade extends BaseFacade<Reflection, ReflectionDTO, ReflectionsState, ReflectionsApiService> {
  constructor(state: ReflectionsState, api: ReflectionsApiService) {
    super(state, api);
  }

  public getReflections$(filter?: RequestPaging): Observable<ICollection<IReflection>> {
    return this.getAll$<ICollection<IReflection>>(filter, { ...new RequestConfig(), url: this.apiService.getReflectionsEndpoint() });
  }

  public getTeamReflection$(params: IGetTeamReflectionEndpointParams): Observable<IReflection> {
    return this.get$<IReflection>("", { ...new RequestConfig(), url: this.apiService.getTeamReflectionEndpoint(params) });
  }

  public patchReflection$({ payload, ...params }: IPatchReflectionEndpointParams & { payload: IPatchReflectionPayload }): Observable<IReflection> {
    return this.patch$<IReflection>("", payload, { ...new RequestConfig(), url: this.apiService.patchReflectionEndpoint(params) });
  }

  public getReflectionUpdates$(filter?: RequestPaging): Observable<ICollection<IReflectionUpdate>> {
    return this.getAll$<ICollection<IReflectionUpdate>>(filter, { ...new RequestConfig(), url: this.apiService.getReflectionUpdatesEndpoint() });
  }

  public postReflectionUpdate$({ payload, ...params }: IPostReflectionUpdateEndpointParams & { payload: IPostReflectionUpdatePayload }): Observable<IReflectionUpdate> {
    return this.post$<IReflectionUpdate>(payload, { ...new RequestConfig(), url: this.apiService.postReflectionUpdateEndpoint(params) });
  }

  public patchReflectionUpdate$({
    payload,
    ...params
  }: IPatchReflectionUpdateEndpointParams & { payload: IPatchReflectionUpdatePayload }): Observable<IPatchReflectionUpdateResponse> {
    return this.patch$<IPatchReflectionUpdateResponse>(params.updateId, payload, { ...new RequestConfig(), url: this.apiService.patchReflectionUpdateEndpoint(params) });
  }

  public postReflectionQuestion$<T extends ReflectionQuestionType>({
    payload,
    ...params
  }: IPostReflectionQuestionEndpointParams & { payload: PostReflectionQuestionPayload<T> }): Observable<unknown> {
    return this.post$<unknown>(payload, { ...new RequestConfig(), url: this.apiService.postReflectionQuestionEndpoint(params) });
  }

  public patchReflectionQuestion$<T extends ReflectionQuestionType>({
    payload,
    ...params
  }: IPatchReflectionQuestionEndpointParams & { payload: Partial<PostReflectionQuestionPayload<T>> }): Observable<ReflectionQuestion<T>> {
    return this.patch$<ReflectionQuestion<T>>("", payload, { ...new RequestConfig(), url: this.apiService.patchReflectionQuestionEndpoint(params) });
  }

  public deleteReflectionQuestion$(params: IDeleteReflectionQuestionEndpointParams): Observable<void> {
    return this.delete$<void>("", { ...new RequestConfig(), url: this.apiService.deleteReflectionQuestionEndpoint(params) });
  }
}
